import { flushGuestJourney } from "@/lib/guest/guestJourney";
import { peekGuestToken } from "@/lib/guest/guestSession";
import { API_BASE_URL, CLIENT_PLATFORM } from "@/lib/config";
import { getSupabaseAccessToken } from "@/lib/auth/supabase";
import { trackApiError } from "@/lib/analytics/game-events";
import type { paths } from "@/types/api.generated";

export type HttpMethod = "get" | "post" | "put" | "patch" | "delete";

export type PathsWithMethod<M extends HttpMethod> = {
  [P in keyof paths]: M extends keyof paths[P] ? P : never;
}[keyof paths];

type Operation<M extends HttpMethod, P extends keyof paths> = paths[P][M];

type JsonContent<T> = T extends { content: { "application/json": infer R } }
  ? R
  : T extends { content: { "application/*+json": infer R } }
    ? R
    : never;

type ResponseFrom<Op> = Op extends { responses: infer R }
  ? R extends { 200: infer R200 }
    ? JsonContent<R200>
    : R extends { 201: infer R201 }
      ? JsonContent<R201>
      : R extends { 202: infer R202 }
        ? JsonContent<R202>
        : R extends { 204: unknown }
          ? void
          : R extends { default: infer RDefault }
            ? JsonContent<RDefault>
            : unknown
  : unknown;

type RequestBodyFrom<Op> = Op extends {
  requestBody?: { content: { "application/json": infer B } };
}
  ? B
  : Op extends { requestBody?: { content: { "application/*+json": infer B } } }
    ? B
    : undefined;

type QueryParamsFrom<Op> = Op extends { parameters?: { query?: infer Q } }
  ? Q
  : undefined;

type PathParamsFrom<Op> = Op extends { parameters?: { path?: infer P } }
  ? P
  : undefined;

export type ApiRequestOptions<
  M extends HttpMethod,
  P extends PathsWithMethod<M>,
> = {
  params?: PathParamsFrom<Operation<M, P>>;
  query?: QueryParamsFrom<Operation<M, P>>;
  body?: RequestBodyFrom<Operation<M, P>> | BodyInit | null;
  headers?: HeadersInit;
  // TODO: Implement auth handling - when false, skip credentials/token injection for public endpoints
  auth?: boolean;
  signal?: AbortSignal;
};

export type ApiResponse<
  M extends HttpMethod,
  P extends PathsWithMethod<M>,
> = ResponseFrom<Operation<M, P>>;

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;

    // Capture stack trace for cleaner debugging (V8 environments)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }
}

function buildUrl(path: string, params?: Record<string, unknown>, query?: Record<string, unknown>) {
  let urlPath = path;
  if (params) {
    urlPath = urlPath.replace(/\{([^}]+)\}/g, (_match, key) => {
      const value = params[key];
      if (value === undefined || value === null) {
        throw new Error(`Missing API path parameter: ${key}`);
      }
      return encodeURIComponent(String(value));
    });
  }

  const url = new URL(
    path.startsWith("http") ? urlPath : `${API_BASE_URL}${urlPath}`,
  );
  if (/\{[^}]+\}/.test(urlPath)) throw new Error('Unresolved API path parameter');

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item !== undefined && item !== null) {
            url.searchParams.append(key, String(item));
          }
        });
        return;
      }
      url.searchParams.set(key, String(value));
    });
  }

  return url.toString();
}

async function parseResponse(response: Response) {
  if (response.status === 204) {
    return undefined;
  }
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    return response.json();
  }
  return response.text();
}

async function request<M extends HttpMethod, P extends PathsWithMethod<M>>(
  method: M,
  path: P,
  options: ApiRequestOptions<M, P> = {},
): Promise<ApiResponse<M, P>> {
  const headers = new Headers(options.headers);
  // An account's origin is recorded only on the winning backend insert.
  // This declaration is not an authorization or device-trust signal.
  headers.set('x-quizball-platform', CLIENT_PLATFORM);
  if (String(path).startsWith("/api/v1/auth/") || String(path) === "/api/v1/users/me") {
    // Give preceding play events a bounded chance to persist before signup claims the journey.
    await Promise.race([flushGuestJourney(), new Promise<void>(resolve => setTimeout(resolve, 1500))]);
  }
  const guestToken = peekGuestToken();
  if (guestToken && (String(path).startsWith("/api/v1/auth/") || String(path) === "/api/v1/users/me")) {
    headers.set("x-guest-token", guestToken);
  }
  if (options.auth !== false && !headers.has("Authorization")) {
    const accessToken = typeof window !== "undefined" ? await getSupabaseAccessToken() : null;
    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }
  }

  let body = options.body as
    | BodyInit
    | Record<string, unknown>
    | null
    | undefined;

  // Only JSON-stringify plain objects, not BodyInit types like FormData, Blob, etc.
  const isPlainObject =
    body !== null &&
    typeof body === "object" &&
    body.constructor === Object;

  if (isPlainObject) {
    body = JSON.stringify(body) as BodyInit;
    if (!headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
  }

  const response = await fetch(buildUrl(String(path), options.params as Record<string, unknown> | undefined, options.query as Record<string, unknown> | undefined), {
    method: method.toUpperCase(),
    headers,
    body: body as BodyInit | null | undefined,
    signal: options.signal,
    credentials: "include",
  });

  const data = await parseResponse(response);

  if (!response.ok) {
    // Analytics: capture API failures for funnel/error analysis.
    // `code` from the standard error envelope `{code, message, …}` falls
    // back to undefined when the response shape is different.
    try {
      const codeFromBody = data && typeof data === 'object' && 'code' in data && typeof (data as { code: unknown }).code === 'string'
        ? (data as { code: string }).code
        : undefined;
      trackApiError(String(path), response.status, codeFromBody, method);
    } catch {
      /* analytics best-effort */
    }
    throw new ApiError("Request failed", response.status, data);
  }

  return data as ApiResponse<M, P>;
}

export const api = {
  request,
  GET: <P extends PathsWithMethod<"get">>(
    path: P,
    options?: ApiRequestOptions<"get", P>,
  ) => request("get", path, options),
  POST: <P extends PathsWithMethod<"post">>(
    path: P,
    options?: ApiRequestOptions<"post", P>,
  ) => request("post", path, options),
  PUT: <P extends PathsWithMethod<"put">>(
    path: P,
    options?: ApiRequestOptions<"put", P>,
  ) => request("put", path, options),
  PATCH: <P extends PathsWithMethod<"patch">>(
    path: P,
    options?: ApiRequestOptions<"patch", P>,
  ) => request("patch", path, options),
  DELETE: <P extends PathsWithMethod<"delete">>(
    path: P,
    options?: ApiRequestOptions<"delete", P>,
  ) => request("delete", path, options),
};
